const { floor, min } = Math;

// days: days in the month, offset: days from beginning of year to start of month
const months: { days: number; offset: number }[] = [
  31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31,
].reduce(
  (output, days) => {
    const prev = output[output.length - 1];
    const offset = prev ? prev.days + prev.offset : 0;
    output.push({ days, offset });
    return output;
  },
  [] as { days: number; offset: number }[]
);

// determine if year is a leap year
const leapYear = (y: number) => y % 4 == 0 && (y % 100 != 0 || y % 400 == 0);

// get number of days in month for the given year
const monthDays = (y: number, m: number) =>
  months[m - 1].days + (m == 2 && leapYear(y) ? 1 : 0);

// get days from beginning of year to start of month for the given year
const monthOffset = (y: number, m: number) =>
  months[m - 1].offset + (m > 2 && leapYear(y) ? 1 : 0);

// get the weekday of the given epoch day
// Jan 1, 1 AD was a Monday; thus, (e - 0) refers to Monday, and (e - 6) refers to Sunday
const weekDay = (e: number) => (((e - 6) % 7) + 7) % 7;

// get the epoch day from year, month, date (days since Jan 1, 1 AD)
const toEpoch = (y: number, m: number, d: number) => {
  y += floor((m - 1) / 12) - 1;
  m = ((((m - 1) % 12) + 12) % 12) + 1;
  return (
    y * 365 +
    floor(y / 4) -
    floor(y / 100) +
    floor(y / 400) +
    monthOffset(y + 1, m) +
    d -
    1
  );
};

// convert the given epoch day into year, month, date
const toYMD = (e: number) => {
  let y: number,
    m: number = 12,
    d: number;
  d = floor(e / 146097);
  y = d * 400;
  e -= d * 146097;
  d = min(floor(e / 36524), 3);
  y += d * 100;
  e -= d * 36524;
  d = floor(e / 1461);
  y += d * 4;
  e -= d * 1461;
  d = min(floor(e / 365), 3);
  y += d + 1;
  d = e - d * 365;
  while (d < monthOffset(y, m)) m--;
  d -= monthOffset(y, m) - 1;
  return { y, m, d };
};

/** Simple date library for quick-n-dirty date manipulation */
export class SimpleDate {
  #e: number = 0;
  #y: number = 0;
  #m: number = 0;
  #d: number = 0;
  #date: Date | null = null;
  #dateUTC: Date | null = null;

  /** Epoch (Assignable, days since January 1st, 1 AD) */
  get epoch(): number {
    return this.#e;
  }
  /** Year (Assignable, full year) */
  get year(): number {
    return this.#y;
  }
  /** Month (Assignable, starts at 1) */
  get month(): number {
    return this.#m;
  }
  /** Day in month (Assignable, starts at 1) */
  get day(): number {
    return this.#d;
  }

  set epoch(e: number) {
    this.#e = e | 0;
    const { y, m, d } = toYMD(this.#e);
    this.#y = y;
    this.#m = m;
    this.#d = d;
    this.#date = this.#dateUTC = null;
  }
  set year(y: number) {
    this.epoch = toEpoch(y | 0, this.#m, this.#d);
  }
  set month(m: number) {
    this.epoch = toEpoch(this.#y, m | 0, this.#d);
  }
  set day(d: number) {
    this.epoch = toEpoch(this.#y, this.#m, d | 0);
  }

  /** Get the day of the week (0:Sun, 1:Mon, 2:Tue, 3:Wed, 4:Thu, 5:Fri, 6:Sat, 7:Sun) */
  get weekday() {
    return weekDay(this.#e);
  }
  /** Get how many days are in this date's month */
  get daysInMonth() {
    return monthDays(this.#y, this.#m);
  }
  /** Get if this date's year is a leapyear */
  get isLeapYear() {
    return leapYear(this.#y);
  }
  /** Get the number of days since the beginning of the year */
  get yearDay() {
    return monthOffset(this.#y, this.#m) + this.#d - 1;
  }
  /** Get a Date object for this date in the current timezone */
  get date() {
    if (!this.#date) {
      this.#date = new Date(0);
      // setFullYear does not treat year 0-99 as 1900-1999
      this.#date.setFullYear(this.#y, this.#m - 1, this.#d);
    }
    return this.#date;
  }
  /** Get a Date object for this date in UTC-format */
  get dateUTC() {
    if (!this.#dateUTC) {
      this.#dateUTC = new Date(0);
      // setUTCFullYear does not treat year 0-99 as 1900-1999
      this.#dateUTC.setUTCFullYear(this.#y, this.#m - 1, this.#d);
    }
    return this.#dateUTC;
  }

  /** Set all properties by year / month / day, by epoch day, from another SimpleDate, or from a Date */
  set(y?: number | SimpleDate | Date, m?: number, d?: number) {
    if (y === undefined) y = new Date();

    if (y instanceof Date) {
      ({ y, m, d } = {
        y: y.getFullYear(),
        m: y.getMonth() + 1,
        d: y.getDate(),
      });
    } else if (y instanceof SimpleDate) {
      ({ year: y, month: m, day: d } = y);
    } else if (m === undefined && d === undefined) {
      ({ y, m, d } = toYMD(y));
    } else {
      m ??= 1;
      d ??= 1;
    }

    this.epoch = toEpoch(y, m, d);
  }

  /** Clone this SimpleDate object */
  copy() {
    return new SimpleDate(this);
  }

  /** Construct a new SimpleDate. Accepts the same parameters as set() */
  constructor(y?: number | SimpleDate | Date, m?: number, d?: number) {
    this.set(y, m, d);
  }
}

/** Quick date generation utility */
export const date = Object.assign(
  function (y?: number | SimpleDate | Date, m?: number, d?: number) {
    return new SimpleDate(y, m, d);
  },
  {
    today: () => new SimpleDate(),
    fromDate: (d: Date) => new SimpleDate(d),
    fromUTCDate: (d: Date) =>
      new SimpleDate(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate()),
  }
);
